import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { SupervsorAlerts, TEmployeeFormData } from "../Employee.model";
import { useQuery } from "@tanstack/react-query";
import { requests } from "../../../services/http-common";
import { useEffect } from "react";
import { EmployeeRoles } from "../../../models/Employee";
import { phoneRegex } from "../../../constants/regex";

const userRoleOptions = [
  {
    label: "Assistant Manager",
    value: EmployeeRoles.ASSISTANT_MANAGER,
  },
  {
    label: "HR Manager",
    value: EmployeeRoles.HR_MANAGER,
  },
  {
    label: "Cleaning Technician",
    value: EmployeeRoles.CLEANING_TECHNICIAN,
  },
  {
    label: "Sales Manager",
    value: EmployeeRoles.SALES_MANAGER,
  },
  {
    label: "Supervisor",
    value: EmployeeRoles.SUPERVISOR,
  },
  {
    label: "Director of Operations",
    value: EmployeeRoles.DOO,
  },
  {
    label: "Chief Operation Officer",
    value: EmployeeRoles.COO,
  },
  {
    label: "Finance Manager ",
    value: EmployeeRoles.FINANCE,
  },

  {
    label: "Administrator",
    value: EmployeeRoles.SUPERADMIN,
  },
  {
    label: "Manager",
    value: EmployeeRoles.MANAGER,
  },
  {
    label: "Sales Representative",
    value: EmployeeRoles.SALES_REPRESENTATIVE,
  },
];

const disallowedDomains = ["example.com", "example.net", "example.org"];

export const useEmployeeForm = (employeeId?: string) => {
  const formSchema = Yup.object().shape({
    firstName: Yup.string().required("Field is required"),
    lastName: Yup.string().required("Field is required"),
    role: Yup.string().nullable(true),
    birthDate: Yup.date().nullable(true),
    dlStateN: Yup.string().nullable(true),
    ssn: Yup.string().nullable(true).min(9, "Please provide at least 9 digits"),
    hireDate: Yup.date().nullable(true),
    email: Yup.string()
      .test("not-valid-domain", "Wrong email", (value) => {
        if (!value) return true;
        return !disallowedDomains.some((domain) => value.includes(domain));
      })
      .email("Wrong email")
      .required("Field is required"),
    addressLine1: Yup.string().nullable(true),
    addressLine2: Yup.string().nullable(true),
    city: Yup.string().nullable(true),
    state: Yup.string().nullable(true),
    ZIP: Yup.string().nullable(true),
    homePhone: Yup.string()
      .matches(phoneRegex, "Invalid phone number")
      .nullable(true),
    workPhone: Yup.string()
      .matches(phoneRegex, "Invalid phone number")
      .nullable(true),
    emergencyContact: Yup.string()
      .matches(phoneRegex, "Invalid phone number")
      .nullable(true),
    emergencyContactName: Yup.string().nullable(true),
    payRate: Yup.number().nullable(true),
    overTimeRate: Yup.number().nullable(true),
    desiredHoursPerWeek: Yup.number().nullable(true),
    accountName: Yup.string().nullable(true),
    routingNumber: Yup.string().nullable(true),
    accountNumber: Yup.string().nullable(true),
    // confirmAccountNumber: Yup.string()
    //   .oneOf([Yup.ref("accountNumber"), null], "Account Numbers must match")
    //   .required("Field is required"),
    confirmAccountNumber: Yup.string().when("accountNumber", {
      is: (val: string) => val && val.length > 0,
      then: Yup.string()
        .oneOf([Yup.ref("accountNumber"), null], "Account Numbers must match")
        .required("Field is required"),
      otherwise: Yup.string(),
    }),
    notes: Yup.string().nullable(true),
    supervisorAlerts: Yup.string().nullable(true),
    mobileAccess: Yup.boolean().nullable(true),
    pdfAttachment: Yup.array().of(Yup.mixed()).nullable(true),
    otherDocumentsArr: Yup.array().of(Yup.mixed()).nullable(true),
  });

  const hookForm = useForm<TEmployeeFormData>({
    resolver: yupResolver(formSchema),
    shouldFocusError: false,
    defaultValues: {
      mobileAccess: false,
      supervisorAlerts: SupervsorAlerts.ALL,
    },
  });

  const { reset, watch } = hookForm;

  const [state] = watch(["state"]);

  const { data: cities } = useQuery({
    queryKey: ["cities", state],
    enabled: !!state,
    queryFn: () => {
      return requests.get(`/administrative-division/usa/cities/${state}`);
    },
    select: (data: { name: string }[]) => {
      return data.map((s) => ({ label: s.name, value: s.name }));
    },
  });

  const { refetch } = useQuery({
    queryKey: [`employee-form-${employeeId}`],
    queryFn: () => {
      return requests.get(`/user/${employeeId}`);
    },
    enabled: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false,
    onSuccess: (data) => {
      reset({
        ...data,
        birthDate: new Date(data.birthDate),
        hireDate: new Date(data.hireDate),
        pdfAttachmentsArr: data.pdfAttachmentsArr?.map((doc: string) => ({
          name: doc.slice(doc.lastIndexOf("/") + 1),
          path: doc,
        })),
        otherDocumentsArr: data.otherDocumentsArr?.map((doc: string) => ({
          name: doc.slice(doc.lastIndexOf("/") + 1),
          path: doc,
        })),
      });
    },
  });

  useEffect(() => {
    if (employeeId) {
      refetch();
    }
  }, [employeeId, refetch]);

  return {
    hookForm,
    userRoleOptions,
    cities,
  };
};
